export type NullableNumber = number | null;

export interface AprLtvFilters {
  minApr: NullableNumber;
  maxApr: NullableNumber;
  minLtv: NullableNumber;
  maxLtv: NullableNumber;
}

export interface LendingOpportunitiesFilters extends AprLtvFilters, Omit<LoanListOptionGroups, 'loanTypes'> {
  loanTypes: string[];
}

/** The checkbox groups the Lend page sends besides its own loan-type `type` param. */
export const LENDING_OPPORTUNITY_EXTRA_GROUPS: LoanListGroupKey[] = ['categories', 'interestTypes', 'repaymentTypes', 'assetTypes'];

export type MyLendingFilters = AprLtvFilters;
export type AllLoansFilters = AprLtvFilters;

export type FilterBadgeKind = 'type' | 'apr' | 'ltv' | 'search' | 'action' | 'date' | 'option' | 'status';

export interface FilterBadge {
  id: string;
  kind: FilterBadgeKind;
  label: string;
  /**
   * Used by the parent component to remove a specific filter.
   * - `type`: string (type value)
   * - `option`: LoanListOptionPayload (a checkbox group and its value)
   * - `apr`/`ltv`: boolean true
   */
  payload?: unknown;
}

export function normalizeNullableNumber(value: unknown): NullableNumber {
  if (value === null || typeof value === 'undefined' || value === '') return null;
  const asNumber = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
}

export function hasAnyAprLtvValue(filters: Partial<AprLtvFilters> | null | undefined): boolean {
  if (!filters) return false;
  return (
    filters.minApr != null ||
    filters.maxApr != null ||
    filters.minLtv != null ||
    filters.maxLtv != null
  );
}

export function hasAnyLendingOpportunitiesValue(filters: Partial<LendingOpportunitiesFilters> | null | undefined): boolean {
  if (!filters) return false;
  const types = Array.isArray(filters.loanTypes) ? filters.loanTypes : [];
  const effectiveTypes = types.filter((t) => (t ?? '').toString().trim().toLowerCase() !== 'all');
  return effectiveTypes.length > 0 || hasAnyLoanListOption({ ...filters, loanTypes: [] }) || hasAnyAprLtvValue(filters);
}

function formatRange(min: NullableNumber, max: NullableNumber, suffix = ''): string {
  const minLabel = min != null ? `${min}${suffix}` : '-';
  const maxLabel = max != null ? `${max}${suffix}` : '-';
  return `${minLabel} - ${maxLabel}`;
}

function opportunityTypeLabel(type: string): string {
  switch ((type ?? '').toString().trim().toLowerCase()) {
    case 'refinance':
      return 'Refinance';
    case 'resale':
      return 'Resale';
    case 'loan':
      return 'New Loan';
    default:
      return type;
  }
}

export function buildAprLtvBadges(filters: Partial<AprLtvFilters>): FilterBadge[] {
  const badges: FilterBadge[] = [];

  if (filters.minApr != null || filters.maxApr != null) {
    badges.push({
      id: 'apr',
      kind: 'apr',
      label: `APR: ${formatRange(filters.minApr ?? null, filters.maxApr ?? null, '%')}`
    });
  }

  if (filters.minLtv != null || filters.maxLtv != null) {
    badges.push({
      id: 'ltv',
      kind: 'ltv',
      label: `LTV: ${formatRange(filters.minLtv ?? null, filters.maxLtv ?? null, '%')}`
    });
  }

  return badges;
}

export function buildLendingOpportunitiesBadges(filters: Partial<LendingOpportunitiesFilters>): FilterBadge[] {
  const badges: FilterBadge[] = [];

  const types = Array.isArray(filters.loanTypes) ? filters.loanTypes : [];
  for (const rawType of types) {
    const normalized = (rawType ?? '').toString().trim().toLowerCase();
    if (!normalized || normalized === 'all') continue;
    badges.push({
      id: `type:${normalized}`,
      kind: 'type',
      label: opportunityTypeLabel(normalized),
      payload: normalized
    });
  }

  badges.push(...buildLoanListOptionBadges(filters, LENDING_OPPORTUNITY_EXTRA_GROUPS));
  badges.push(...buildAprLtvBadges(filters));
  return badges;
}


// ---- Borrowing / Lending lists: checkbox groups --------------------------------------------
// Applied by the API (CATEGORIES, LOAN_TYPES, INTEREST_TYPES, REPAYMENT_TYPES, ASSET_TYPES);
// these only shape the panel and chips. Each group is multi-select: a loan matches when it is any
// of the options ticked in a group, and every group with a tick must match.

export type LoanListType = 'loan' | 'refinance' | 'resale' | 'syndicate';
export type LoanInterestType = 'fixed' | 'flexible';
export type LoanRepaymentType = 'monthly' | 'end';
export type LoanAssetType = 'single' | 'bundle';

export interface LoanListOptionGroups {
  /** Collateral asset categories (Gold, Wine…) - open-ended, so the options come from the counts. */
  categories: string[];
  loanTypes: LoanListType[];
  interestTypes: LoanInterestType[];
  repaymentTypes: LoanRepaymentType[];
  assetTypes: LoanAssetType[];
}

export type LoanListGroupKey = keyof LoanListOptionGroups;

export type LoanListFilters = AprLtvFilters & LoanListOptionGroups;

/** Per-option counts from the API's `filter_counts`, keyed by group then option value. */
export type LoanListFilterCounts = Partial<Record<string, Record<string, number>>>;

export interface LoanListOptionPayload {
  group: LoanListGroupKey;
  value: string;
}

export interface LoanListOptionGroup {
  key: LoanListGroupKey;
  title: string;
  /** The group's key in the API's `filter_counts`. */
  countKey: string;
  /** Request parameter carrying the ticked values. */
  param: string;
  options: Array<{ value: string; label: string }>;
  /** Options are whatever the API counted (plus anything ticked), not a fixed list. */
  dynamic?: boolean;
}

export const LOAN_LIST_OPTION_GROUPS: LoanListOptionGroup[] = [
  {
    key: 'categories',
    title: 'Category.',
    countKey: 'categories',
    param: 'CATEGORIES',
    options: [],
    dynamic: true
  },
  {
    key: 'loanTypes',
    title: 'Loan types.',
    countKey: 'loan_types',
    param: 'LOAN_TYPES',
    options: [
      { value: 'loan', label: 'Normal' },
      { value: 'refinance', label: 'Refinance' },
      { value: 'resale', label: 'Resale' },
      { value: 'syndicate', label: 'Syndicate' }
    ]
  },
  {
    key: 'interestTypes',
    title: 'Interest type.',
    countKey: 'interest_types',
    param: 'INTEREST_TYPES',
    options: [
      { value: 'fixed', label: 'Fixed' },
      { value: 'flexible', label: 'Flexible' }
    ]
  },
  {
    key: 'repaymentTypes',
    title: 'Repayment.',
    countKey: 'repayment_types',
    param: 'REPAYMENT_TYPES',
    options: [
      { value: 'monthly', label: 'Monthly' },
      { value: 'end', label: 'End of loan' }
    ]
  },
  {
    key: 'assetTypes',
    title: 'Loan type.',
    countKey: 'asset_types',
    param: 'ASSET_TYPES',
    options: [
      { value: 'single', label: 'Single' },
      { value: 'bundle', label: 'Bundle' }
    ]
  }
];

export const LOAN_LIST_GROUP_KEYS: LoanListGroupKey[] = LOAN_LIST_OPTION_GROUPS.map((group) => group.key);

/** The ticked values of each checkbox group in a filter form value (or applied filters). */
export function pickLoanListOptions(value: any): LoanListOptionGroups {
  const pick = (key: LoanListGroupKey) => (Array.isArray(value?.[key]) ? [...value[key]] : []);
  return {
    categories: pick('categories'),
    loanTypes: pick('loanTypes'),
    interestTypes: pick('interestTypes'),
    repaymentTypes: pick('repaymentTypes'),
    assetTypes: pick('assetTypes')
  };
}

export function emptyLoanListOptions(): LoanListOptionGroups {
  return { categories: [], loanTypes: [], interestTypes: [], repaymentTypes: [], assetTypes: [] };
}

export function emptyLoanListFilters(): LoanListFilters {
  return { minApr: null, maxApr: null, minLtv: null, maxLtv: null, ...emptyLoanListOptions() };
}

export function hasAnyLoanListOption(filters: Partial<Record<LoanListGroupKey, string[]>> | null | undefined): boolean {
  return LOAN_LIST_GROUP_KEYS.some((key) => (filters?.[key]?.length ?? 0) > 0);
}

export function hasAnyLoanListValue(filters: Partial<LoanListFilters> | null | undefined): boolean {
  if (!filters) return false;
  return hasAnyAprLtvValue(filters) || hasAnyLoanListOption(filters);
}

/** `filters` with one ticked option removed - what a dismissed `option` badge leaves behind. */
export function withoutLoanListOption<T extends Partial<LoanListOptionGroups>>(filters: T, payload: LoanListOptionPayload): T {
  const current = (filters[payload.group] as string[] | undefined) ?? [];
  return { ...filters, [payload.group]: current.filter((value) => value !== payload.value) };
}

/** The request parameters for the ticked options, e.g. { INTEREST_TYPES: 'fixed' }. */
export function loanListOptionParams(filters: Partial<LoanListOptionGroups> | null | undefined): Record<string, string> {
  const params: Record<string, string> = {};
  for (const group of LOAN_LIST_OPTION_GROUPS) {
    const values = filters?.[group.key];
    if (Array.isArray(values) && values.length) params[group.param] = values.join(',');
  }
  return params;
}

export function buildLoanListOptionBadges(filters: Partial<Record<LoanListGroupKey, string[]>>, groups: LoanListGroupKey[] = LOAN_LIST_GROUP_KEYS): FilterBadge[] {
  const badges: FilterBadge[] = [];
  for (const group of LOAN_LIST_OPTION_GROUPS) {
    if (!groups.includes(group.key)) continue;
    for (const value of filters[group.key] ?? []) {
      const option = group.options.find((item) => item.value === value);
      const payload: LoanListOptionPayload = { group: group.key, value };
      badges.push({ id: `${group.key}:${value}`, kind: 'option', label: option?.label ?? value, payload });
    }
  }
  return badges;
}

export function buildLoanListBadges(filters: Partial<LoanListFilters>): FilterBadge[] {
  return [...buildLoanListOptionBadges(filters), ...buildAprLtvBadges(filters)];
}
